import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

function formatDate(value) {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function UserDetails(props) {
  const [state, setState] = useState({
    profile: {},
    repository: [],
    loading: true,
    error: "",
    heatmap: null,
    heatmapError: "",
    repoFilter: "",
    repoSort: "updated",
    visibleRepos: 5
  });

  useEffect(() => {
    const uid = props.match.params.uid;

    const getUser = async () => {
      setState((prev) => ({ ...prev, loading: true, error: "" }));

      try {
        const profileResponse = await fetch(`https://api.github.com/users/${uid}`);
        const repoResponse = await fetch(`https://api.github.com/users/${uid}/repos?per_page=100&sort=updated`);
        const profile = await profileResponse.json();
        const repos = await repoResponse.json();

        if (profile.message) {
          throw new Error(profile.message);
        }

        const token = process.env.REACT_APP_GITHUB_TOKEN;
        let heatmap = null;
        let heatmapError = "";

        if (token) {
          try {
            const heatmapResponse = await fetch("https://api.github.com/graphql", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                query: `query($login: String!) { user(login: $login) { contributionsCollection { contributionCalendar { totalContributions weeks { contributionDays { contributionCount date weekday } } } } } }`,
                variables: { login: uid }
              })
            });

            if (!heatmapResponse.ok) {
              throw new Error(`Heatmap request failed with status ${heatmapResponse.status}`);
            }

            const heatmapData = await heatmapResponse.json();
            heatmap =
              heatmapData &&
              heatmapData.data &&
              heatmapData.data.user &&
              heatmapData.data.user.contributionsCollection &&
              heatmapData.data.user.contributionsCollection.contributionCalendar
                ? heatmapData.data.user.contributionsCollection.contributionCalendar
                : null;

            if (!heatmap) {
              heatmapError = "The token is configured, but the heatmap could not be loaded for this profile yet.";
            }
          } catch (error) {
            heatmap = null;
            heatmapError = "The token is configured, but the heatmap could not be loaded. Please restart the app and try again.";
          }
        }

        setState({
          profile,
          repository: repos,
          loading: false,
          error: "",
          heatmap,
          heatmapError,
          repoFilter: "",
          repoSort: "updated",
          visibleRepos: 5
        });
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message || "Unable to load profile."
        }));
      }
    };

    getUser();
  }, [props.match.params.uid]);

  const languageBreakdown = state.repository.reduce((acc, repo) => {
    const language = repo.language || "Other";
    acc[language] = (acc[language] || 0) + 1;
    return acc;
  }, {});

  const maxLanguage = Math.max(...Object.values(languageBreakdown), 1);
  const loadMoreRepos = () => {
    setState((prev) => ({ ...prev, visibleRepos: prev.visibleRepos + 5 }));
  };

  const filteredRepos = state.repository
    .filter((repo) => {
      const query = state.repoFilter.toLowerCase();
      if (!query) {
        return true;
      }
      return `${repo.name} ${repo.description || ""}`.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      switch (state.repoSort) {
        case "stars":
          return (b.stargazers_count || 0) - (a.stargazers_count || 0);
        case "forks":
          return (b.forks_count || 0) - (a.forks_count || 0);
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        default:
          return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      }
    });

  const downloadProfile = () => {
    window.print();
  };

  return (
    <div className="detail-page">
      <Link className="back-link" to="/">
        <span>←</span> Back to search
      </Link>

      {state.loading ? (
        <div className="detail-grid">
          <div className="skeleton-card" />
          <div className="skeleton-card" />
        </div>
      ) : state.error ? (
        <div className="state-card error-state">
          <h3>Profile unavailable</h3>
          <p>{state.error}</p>
        </div>
      ) : (
        <>
          <section className="profile-hero">
            <img src={state.profile.avatar_url} alt={`${state.profile.login} avatar`} />
            <div>
              <p className="eyebrow">Developer profile</p>
              <h1>{state.profile.name || state.profile.login}</h1>
              <p>{state.profile.bio || "No bio available yet."}</p>
              <div className="profile-stats">
                <span className="stat-pill">Followers {state.profile.followers}</span>
                <span className="stat-pill">Following {state.profile.following}</span>
                <span className="stat-pill">Repos {state.profile.public_repos}</span>
              </div>
              <div className="profile-actions">
                <a
                  className="primary-btn"
                  href={state.profile.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit GitHub profile
                </a>
                <button className="secondary-btn" type="button" onClick={downloadProfile}>
                  Download as PDF
                </button>
              </div>
            </div>
          </section>

          <section className="analytics-grid">
            <article className="detail-card">
              <h2>Analytics dashboard</h2>
              <div className="chart-card">
                <div className="chart-row">
                  <span>Followers</span>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${Math.min((state.profile.followers || 0) / 50) * 100}%` }} />
                  </div>
                </div>
                <div className="chart-row">
                  <span>Repos</span>
                  <div className="bar-track">
                    <div className="bar-fill accent" style={{ width: `${Math.min((state.profile.public_repos || 0) / 50) * 100}%` }} />
                  </div>
                </div>
                <div className="chart-row">
                  <span>Stars</span>
                  <div className="bar-track">
                    <div className="bar-fill muted" style={{ width: `${Math.min((filteredRepos.reduce((total, repo) => total + (repo.stargazers_count || 0), 0) || 0) / 500) * 100}%` }} />
                  </div>
                </div>
              </div>
            </article>

            <article className="detail-card">
              <h2>Language breakdown</h2>
              <div className="chart-card">
                {Object.entries(languageBreakdown).map(([language, count]) => (
                  <div key={language} className="chart-row">
                    <span>{language}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${(count / maxLanguage) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="detail-grid">
            <article className="detail-card">
              <h2>About</h2>
              <ul className="detail-list">
                <li>
                  <span>Username</span>
                  <strong>{state.profile.login}</strong>
                </li>
                <li>
                  <span>Company</span>
                  <strong>{state.profile.company || "—"}</strong>
                </li>
                <li>
                  <span>Location</span>
                  <strong>{state.profile.location || "—"}</strong>
                </li>
                <li>
                  <span>Website</span>
                  <strong>{state.profile.blog || "—"}</strong>
                </li>
              </ul>
            </article>

            <article className="detail-card">
              <h2>Contribution heatmap</h2>
              {state.heatmap ? (
                <div className="heatmap-grid">
                  {state.heatmap.weeks &&
                    state.heatmap.weeks.reduce((days, week) => {
                      if (week.contributionDays) {
                        return days.concat(week.contributionDays);
                      }
                      return days;
                    }, []).map((day, index) => (
                      <span
                        key={`${day.date}-${index}`}
                        className={`heatmap-cell level-${Math.min(4, Math.round((day.contributionCount || 0) / 4))}`}
                        title={`${day.date}: ${day.contributionCount} contributions`}
                      />
                    ))}
                </div>
              ) : (
                <p>
                  {state.heatmapError || "Add a GitHub token in the app environment to enable the contribution heatmap."}
                </p>
              )}
            </article>
          </section>

          <section className="detail-card">
            <div className="section-heading">
              <h2>Repositories</h2>
              <p>Filter and sort your way through public repos.</p>
            </div>
            <div className="repo-filters">
              <input
                type="text"
                placeholder="Filter repositories"
                value={state.repoFilter || ""}
                onChange={(e) => {
                  const value = e && e.target ? e.target.value : "";
                  setState((prev) => ({ ...prev, repoFilter: value }));
                }}
              />
              <select
                value={state.repoSort || "updated"}
                onChange={(e) => {
                  const value = e && e.target ? e.target.value : "updated";
                  setState((prev) => ({ ...prev, repoSort: value }));
                }}
              >
                <option value="updated">Recently updated</option>
                <option value="stars">Most stars</option>
                <option value="forks">Most forks</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div className="repo-grid">
              {filteredRepos.slice(0, state.visibleRepos).map((repo) => (
                <a
                  key={repo.id}
                  className="repo-card"
                  href={repo.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div>
                    <h3>{repo.name}</h3>
                    <p>{repo.description || "No description available."}</p>
                  </div>
                  <div className="repo-meta">
                    {repo.language && <span className="language-badge">{repo.language}</span>}
                    <span>★ {repo.stargazers_count}</span>
                    <span>↩ {repo.forks_count}</span>
                    <span>Updated {formatDate(repo.updated_at)}</span>
                  </div>
                </a>
              ))}
            </div>
            {filteredRepos.length > state.visibleRepos && (
              <div className="repo-actions">
                <button className="secondary-btn" type="button" onClick={loadMoreRepos}>
                  Load more repositories
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default UserDetails;
